<template>
  <div class="w-full sm:w-1/2 lg:w-1/3 px-2 mb-4">
    <v-card class="stat-card" flat>
      
      <div v-if="title == 'Reviews Gathered'">
        <div class="stat-header">
          <div>
            <div class="stat-title">{{ title }}:</div>
            <div class="stat-value">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ value || '-' }}
                    </span>
                </template>

                <span>
                    <span>{{ tooltiptext }}</span>
                </span>
              </v-tooltip>

            </div>
          </div>

          <div>
            <div class="stat-title">Negative Feedback:</div>
            <div class="stat-value">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ negative_feedback_count || '-' }}
                    </span>
                </template>

                <span>
                    Total negative feedback received from customers.
                </span>
              </v-tooltip>
            </div>
          </div>

          <v-icon class="stat-icon">{{ icon }}</v-icon>
        </div>
      </div>
      
      <div v-else-if="title == 'Total Calls'">
        <div class="stat-header">
          <div>
            <div class="stat-title">{{ title }}:</div>
            <div class="stat-value">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ value || '-' }}
                    </span>
                </template>

                <span>
                    <span>{{ tooltiptext }}</span>
                </span>
              </v-tooltip>

            </div>
          </div>

          <div>
            <div class="stat-title">Spam Calls:</div>
            <div class="stat-value">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ total_spam || '-' }}
                    </span>
                </template>

                <span>
                    Total Spam calls within the selected period.
                </span>
              </v-tooltip>
            </div>
          </div>

          <v-icon class="stat-icon">{{ icon }}</v-icon>
        </div>
      </div>

      <div v-else-if="title != 'Credited'" class="stat-header">
        <div>
          <div class="stat-title">{{ title }}</div>
          <div v-if="!tooltiptext" class="stat-value">{{ value || '-' }}</div>
          <div v-else class="stat-value">
              <v-tooltip top color="primary">
                  <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" >{{ value || '-' }}</span>
                  </template>
                  <span>{{ tooltiptext }}</span>
              </v-tooltip>
          </div>
        </div>

        <v-icon class="stat-icon">{{ icon }}</v-icon>
      </div>

      <div v-else>
        <div class="stat-header">
          <div>
            <div class="stat-title">{{ title }}:</div>
            <div class="stat-value">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ value || '-' }} <span style="font-size: 18px;"> - ~${{ refunds_amount}}</span>
                    </span>
                </template>

                <span>
                    Approved credits for the selected period. Dollar amounts are estimates using average lead cost; verify actual credits in Google.
                </span>
              </v-tooltip>

            </div>
          </div>

          <div>
            <div class="stat-title">Total:</div>
            <div class="stat-value">
              <v-tooltip bottom color="primary">
                <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ total_refunds || '-' }} <span style="font-size: 18px;"> - ~${{ refunds_amount_total}}</span>
                    </span>
                </template>

                <span>
                    Approved credits since surveys started, including this period. Dollar amounts are estimates, not pending credits.
                </span>
              </v-tooltip>
            </div>
          </div>

          <v-icon class="stat-icon">{{ icon }}</v-icon>
        </div>
      </div>

      <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <span
                  class="stat-change"
                  :class="changeClass"
                >
                  <v-icon small>
                    {{ changeIcon }}
                  </v-icon>

                  <span v-if="isLeadQuality || 1">
                    {{ formattedChange }}
                  </span>

                  <span v-else>
                    {{ change }}%
                  </span>
                </span>
              </div>
          </template>

          <span>
            <span v-if="isLeadQuality || trend === 'stable'">
              No change compared to last month
            </span>

            <span v-else-if="changeClass == 'up'">
              Shows increase compared to last month
            </span>

            <span v-else-if="changeClass == 'down'">
              Shows decrease compared to last month
            </span>
          </span>
        </v-tooltip>

    </v-card>
  </div>
</template>

<script>
export default {
  props: {
    title: String,
    icon: String,
    value: [String, Number],
    current: [String, Number],
    previous: [String, Number],
    change: [String, Number],
    total_refunds: [String, Number],
    refunds_amount: [String, Number],
    refunds_amount_total: [String, Number],
    total_spam: [String, Number],
    negative_feedback_count: [String, Number],
    tooltiptext: [String],
    reverse: Boolean
  },
  computed: {
    isLeadQuality () {
      return this.title === 'Lead Quality'
    },

    trend () {
      // if (!this.isLeadQuality) return null

      if (!this.change || Number(this.change) === 0) return 'stable'

      const isDown = this.reverse
        ? this.current > this.previous
        : this.current < this.previous

      return isDown ? 'down' : 'up'
    },

    changeClass () {
      if (this.isLeadQuality || 1) return this.trend
      return this.isDown ? 'down' : 'up'
    },

    changeIcon () {
      if (this.isLeadQuality || 1) {
        if (this.trend === 'up') return 'mdi-trending-up'
        if (this.trend === 'down') return 'mdi-trending-down'
        return 'mdi-minus'
      }

      return this.isDown ? 'mdi-arrow-down' : 'mdi-arrow-up'
    },

    formattedChange () {
      if (this.isLeadQuality || 1) {
        if (this.trend === 'stable') return 'Stable'
        return `${Math.abs(this.change)}%`
      }

      return `${this.change}%`
    },

    isDown () {
      return this.reverse
        ? this.current > this.previous
        : this.current < this.previous
    }
  }
}
</script>

<style scoped>
.stat-group-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #111827;
}

.stat-card {
  border-radius: 14px;
  padding: 16px;
  background: #f4f7ff;
}

.stat-header {
  display: flex;
  justify-content: space-between;
}

.stat-title {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  margin-top: 4px;
}

.stat-icon {
  background: #d4daf4;
  padding: 8px;
  padding-top: 0px;padding-bottom: 0px;
  height: 40px;
  border-radius: 10px;
  color: #4f46e5;
}

.stat-change {
  margin-top: 8px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  /* 👇 this is what creates the pill/badge look */
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 500;
}

/* UP (green badge) */
.stat-change.up {
  background: #dcfce7;
  color: #16a34a;
}

/* DOWN (red badge) */
.stat-change.down {
  background: #fee2e2;
  color: #dc2626;
}

/* STABLE (yellow badge) */
.stat-change.stable {
  background: #fef9c3;
  color: #ca8a04;
}
</style>